<script>
  export let src;
  export let alt = "";
  /** Incremented on Splide `moved` to reset pan/zoom for every slide instance */
  export let resetEpoch = 0;

  import { onDestroy, onMount, tick } from "svelte";
  import Panzoom from "@panzoom/panzoom";

  let viewportEl;
  let imgEl;
  let minimapImgEl;
  /** @type {import('@panzoom/panzoom').PanzoomObject | null} */
  let panzoom = null;

  let viewportNorm = { left: 0, top: 0, width: 1, height: 1 };
  let rafScheduled = false;
  let lastResetEpoch = -1;

  function scheduleUpdateRect() {
    if (rafScheduled) return;
    rafScheduled = true;
    requestAnimationFrame(() => {
      rafScheduled = false;
      updateViewportRect();
    });
  }

  function updateViewportRect() {
    if (!viewportEl || !imgEl) return;
    const V = viewportEl.getBoundingClientRect();
    const I = imgEl.getBoundingClientRect();
    if (I.width < 1 || I.height < 1) {
      viewportNorm = { left: 0, top: 0, width: 1, height: 1 };
      return;
    }
    const overlapLeft = Math.max(V.left, I.left);
    const overlapRight = Math.min(V.right, I.right);
    const overlapTop = Math.max(V.top, I.top);
    const overlapBottom = Math.min(V.bottom, I.bottom);
    const w = Math.max(0, overlapRight - overlapLeft);
    const h = Math.max(0, overlapBottom - overlapTop);
    viewportNorm = {
      left: (overlapLeft - I.left) / I.width,
      top: (overlapTop - I.top) / I.height,
      width: w / I.width,
      height: h / I.height,
    };
  }

  function onPanzoomChange() {
    scheduleUpdateRect();
  }

  /** Animated zoom/reset uses CSS transition; getBoundingClientRect lags until transform finishes */
  function onImgTransitionEnd(e) {
    if (e.target !== imgEl) return;
    const p = e.propertyName;
    if (p !== "transform" && p !== "-webkit-transform") return;
    rafScheduled = false;
    updateViewportRect();
  }

  $: if (panzoom && resetEpoch !== lastResetEpoch) {
    lastResetEpoch = resetEpoch;
    panzoom.reset({ animate: false });
    tick().then(scheduleUpdateRect);
  }

  let wheelHandler;

  onMount(async () => {
    await tick();
    if (!imgEl || !viewportEl) return;
    panzoom = Panzoom(imgEl, {
      maxScale: 4,
      minScale: 1,
      panOnlyWhenZoomed: true,
      cursor: "grab",
      step: 0.35,
      pinchAndPan: true,
    });
    wheelHandler = (e) => panzoom.zoomWithWheel(e);
    imgEl.addEventListener("panzoomchange", onPanzoomChange);
    imgEl.addEventListener("transitionend", onImgTransitionEnd);
    viewportEl.addEventListener("wheel", wheelHandler, { passive: false });
    scheduleUpdateRect();
  });

  onDestroy(() => {
    if (viewportEl && wheelHandler) {
      viewportEl.removeEventListener("wheel", wheelHandler);
    }
    if (imgEl) {
      imgEl.removeEventListener("panzoomchange", onPanzoomChange);
      imgEl.removeEventListener("transitionend", onImgTransitionEnd);
    }
    panzoom?.destroy();
    panzoom = null;
  });

  function onMinimapPointerDown(e) {
    if (!panzoom || !viewportEl || !imgEl || !minimapImgEl) return;
    e.preventDefault();
    const pr = minimapImgEl.getBoundingClientRect();
    const nx = (e.clientX - pr.left) / pr.width;
    const ny = (e.clientY - pr.top) / pr.height;
    const clampedNx = Math.min(1, Math.max(0, nx));
    const clampedNy = Math.min(1, Math.max(0, ny));
    const V = viewportEl.getBoundingClientRect();
    const I = imgEl.getBoundingClientRect();
    const targetX = I.left + clampedNx * I.width;
    const targetY = I.top + clampedNy * I.height;
    const centerX = V.left + V.width / 2;
    const centerY = V.top + V.height / 2;
    const s = panzoom.getScale();
    const p = panzoom.getPan();
    panzoom.pan(p.x + (centerX - targetX) / s, p.y + (centerY - targetY) / s, {
      relative: false,
      force: true,
    });
  }

  function zoomIn() {
    panzoom?.zoomIn({ animate: true });
  }
  function zoomOut() {
    panzoom?.zoomOut({ animate: true });
  }
  function zoomReset() {
    panzoom?.reset({ animate: true });
  }
</script>

<div class="gallery-zoom-root">
  <div class="viewport" bind:this={viewportEl}>
    <img
      bind:this={imgEl}
      class="gallery-zoom-img"
      {src}
      {alt}
      draggable="false"
    />
  </div>

  <div class="zoom-toolbar panzoom-exclude" aria-label="Image zoom controls">
    <button type="button" class="zoom-btn" on:click={zoomIn} aria-label="Zoom in">+</button>
    <button type="button" class="zoom-btn" on:click={zoomOut} aria-label="Zoom out">−</button>
    <button type="button" class="zoom-btn zoom-btn-reset" on:click={zoomReset} aria-label="Reset zoom">↺</button>
  </div>

  <div
    class="minimap panzoom-exclude"
    aria-hidden="true"
    on:pointerdown|stopPropagation={onMinimapPointerDown}
  >
    <img bind:this={minimapImgEl} class="minimap-img" {src} alt="" />
    <div
      class="minimap-viewport"
      style="left:{viewportNorm.left * 100}%; top:{viewportNorm.top * 100}%; width:{viewportNorm.width *
        100}%; height:{viewportNorm.height * 100}%;"
    />
  </div>
</div>

<style>
  .gallery-zoom-root {
    --gz-minimap-h: 80px;
    --gz-minimap-gap: 6px;
    --gz-edge: 8px;
    position: relative;
    width: 100%;
    max-width: 100%;
    height: 75vh;
    max-height: 75vh;
    margin: 0 auto;
    touch-action: none;
  }

  .viewport {
    width: 100%;
    height: 100%;
    overflow: hidden;
    position: relative;
    background: rgba(0, 0, 0, 0.2);
  }

  .gallery-zoom-img {
    display: block;
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    margin: 0 auto;
    object-fit: contain;
    user-select: none;
    -webkit-user-drag: none;
  }

  .zoom-toolbar {
    position: absolute;
    right: var(--gz-edge);
    bottom: calc(var(--gz-edge) + var(--gz-minimap-h) + var(--gz-minimap-gap));
    z-index: 2;
    display: flex;
    flex-direction: row;
    gap: 4px;
  }

  .zoom-btn {
    width: 24px;
    height: 24px;
    padding: 0;
    border: 1px solid rgba(255, 255, 255, 0.85);
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    font-size: 1.25rem;
    line-height: 1;
    cursor: pointer;
  }

  .zoom-btn:hover {
    background: rgba(40, 40, 40, 0.85);
  }

  .zoom-btn-reset {
    font-size: 1.1rem;
  }

  .minimap {
    position: absolute;
    right: var(--gz-edge);
    bottom: var(--gz-edge);
    width: 120px;
    height: var(--gz-minimap-h);
    z-index: 2;
    border: 1px solid rgba(255, 255, 255, 0.45);
    border-radius: 4px;
    overflow: hidden;
    background: rgba(0, 0, 0, 0.45);
    cursor: crosshair;
  }

  .minimap-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    pointer-events: none;
    opacity: 0.85;
  }

  .minimap-viewport {
    position: absolute;
    box-sizing: border-box;
    border: 2px solid rgba(255, 255, 255, 0.95);
    background: rgba(255, 255, 255, 0.08);
    pointer-events: none;
  }
</style>
